// ============================================================================
// TASK CONTENT WIDGET - Displays pinned images/markdown from session tasks
// Content is pushed via "Push to Screen" button in SessionTaskDetailModal
// ============================================================================

import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ChevronLeft, ChevronRight, ImageIcon, FileText, Code } from 'lucide-react';
import { useSecondScreenData } from '../../../contexts/SecondScreenContext';
import type { WidgetProps, PinnedContentItem } from '../../../types/second-screen.types';

const MermaidDiagram = React.lazy(() =>
  import('../../diagrams/MermaidDiagram').then(mod => ({ default: mod.MermaidDiagram }))
);

function getPagerIcon(type: PinnedContentItem['type']) {
  if (type === 'image') return <ImageIcon size={10} />;
  if (type === 'html') return <Code size={10} />;
  return <FileText size={10} />;
}

const PinnedImage: React.FC<{ item: PinnedContentItem; isDark: boolean }> = ({ item, isDark }) => (
  <div className="flex flex-col h-full w-full p-2">
    {item.title && (
      <h3 className={`text-sm font-semibold truncate max-w-full shrink-0 mb-1 ${isDark ? 'text-white/80' : 'text-slate-700'}`}>
        {item.title}
      </h3>
    )}
    <div className={`flex-1 min-h-0 flex ${item.description ? 'items-stretch gap-3' : 'items-center justify-center'} overflow-hidden`}>
      <div className={`flex items-center justify-center ${item.description ? 'w-1/2 shrink-0' : 'w-full h-full'}`}>
        <img
          src={item.content}
          alt={item.title || 'Task output'}
          className="max-w-full max-h-full object-contain"
        />
      </div>
      {item.description && (
        <div className={`flex-1 min-w-0 overflow-y-auto text-base leading-relaxed whitespace-pre-wrap p-3 rounded-lg ${
          isDark ? 'bg-white/5 text-white/60' : 'bg-slate-50 text-slate-600'
        }`}>
          <h4 className={`text-sm font-bold uppercase tracking-wide mb-2 ${isDark ? 'text-white/40' : 'text-slate-500'}`}>
            Extracted Context
          </h4>
          {item.description}
        </div>
      )}
    </div>
    <span className={`text-xs shrink-0 mt-1 text-center ${isDark ? 'text-white/30' : 'text-slate-400'}`}>
      {item.source}
    </span>
  </div>
);

function createMarkdownComponents(isDark: boolean) {
  return {
    // eslint-disable-next-line jsx-a11y/heading-has-content
    h1: ({ node, ...props }: any) => <h1 className={`text-3xl font-bold mt-0 mb-3 pb-2 border-b ${isDark ? 'border-white/10' : 'border-slate-200'}`} {...props} />,
    // eslint-disable-next-line jsx-a11y/heading-has-content
    h2: ({ node, ...props }: any) => <h2 className="text-2xl font-semibold mt-4 mb-2" {...props} />,
    // eslint-disable-next-line jsx-a11y/heading-has-content
    h3: ({ node, ...props }: any) => <h3 className="text-xl font-semibold mt-3 mb-1.5" {...props} />,
    p: ({ node, ...props }: any) => <p className="mb-3 leading-relaxed" {...props} />,
    ul: ({ node, ...props }: any) => <ul className="ml-5 mb-3 list-disc" {...props} />,
    ol: ({ node, ...props }: any) => <ol className="ml-5 mb-3 list-decimal" {...props} />,
    li: ({ node, ...props }: any) => <li className="mb-1" {...props} />,
    code: ({ node, inline, ...props }: any) =>
      inline
        ? <code className={`px-1.5 py-0.5 rounded text-sm font-mono ${isDark ? 'bg-white/10' : 'bg-slate-100'}`} {...props} />
        : <code className={`block p-3 rounded text-sm font-mono overflow-auto ${isDark ? 'bg-white/5' : 'bg-slate-50'}`} {...props} />,
    pre: ({ node, ...props }: any) => <pre className="mb-3" {...props} />,
    blockquote: ({ node, ...props }: any) => <blockquote className={`border-l-4 pl-4 ml-0 mb-3 ${isDark ? 'border-white/20 text-white/50' : 'border-slate-300 text-slate-500'}`} {...props} />,
    strong: ({ node, ...props }: any) => <strong className="font-semibold" {...props} />,
    table: ({ node, ...props }: any) => <table className="border-collapse w-full mb-3 text-base" {...props} />,
    thead: ({ node, ...props }: any) => <thead className={isDark ? 'bg-white/5' : 'bg-slate-50'} {...props} />,
    th: ({ node, ...props }: any) => <th className={`border p-2 text-left font-semibold ${isDark ? 'border-white/10' : 'border-slate-200'}`} {...props} />,
    td: ({ node, ...props }: any) => <td className={`border p-2 ${isDark ? 'border-white/10' : 'border-slate-200'}`} {...props} />,
    img: ({ node, ...props }: any) => <img className="max-w-full rounded my-3" {...props} alt={props.alt || ''} />,
  };
}

const PinnedMarkdown: React.FC<{ item: PinnedContentItem; isDark: boolean }> = ({ item, isDark }) => {
  const mdComponents = useMemo(() => createMarkdownComponents(isDark), [isDark]);
  return (
    <div className="flex flex-col h-full p-4 overflow-hidden">
      {item.title && (
        <h3 className={`text-lg font-semibold mb-3 shrink-0 ${isDark ? 'text-white/80' : 'text-slate-700'}`}>
          {item.title}
        </h3>
      )}
      <div className={`flex-1 min-h-0 overflow-y-auto text-base leading-relaxed ${
        isDark ? 'text-white/70' : 'text-slate-600'
      }`}>
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
          {item.content}
        </ReactMarkdown>
      </div>
      <span className={`text-sm mt-2 shrink-0 ${isDark ? 'text-white/30' : 'text-slate-400'}`}>
        {item.source}
      </span>
    </div>
  );
};

const PinnedMermaid: React.FC<{ item: PinnedContentItem; isDark: boolean }> = ({ item, isDark }) => {
  return (
    <div className="flex flex-col items-center justify-center h-full gap-2 p-2">
      {item.title && (
        <h3 className={`text-sm font-semibold ${isDark ? 'text-white/80' : 'text-slate-700'}`}>
          {item.title}
        </h3>
      )}
      <div className="flex-1 min-h-0 w-full overflow-auto flex items-center justify-center">
        <React.Suspense fallback={<div className={`text-xs ${isDark ? 'text-white/30' : 'text-slate-400'}`}>Loading diagram...</div>}>
          <MermaidDiagram code={item.content} />
        </React.Suspense>
      </div>
      <span className={`text-xs ${isDark ? 'text-white/30' : 'text-slate-400'}`}>
        {item.source}
      </span>
    </div>
  );
};

const PinnedHtml: React.FC<{ item: PinnedContentItem; isDark: boolean }> = ({ item, isDark }) => (
  <div className="flex flex-col h-full w-full p-2">
    {item.title && (
      <h3 className={`text-sm font-semibold truncate max-w-full shrink-0 mb-1 ${isDark ? 'text-white/80' : 'text-slate-700'}`}>
        {item.title}
      </h3>
    )}
    <div className="flex-1 min-h-0">
      <iframe
        src={item.content}
        title={item.title || 'Design'}
        sandbox="allow-scripts allow-same-origin"
        className="w-full h-full border-0 rounded bg-white"
      />
    </div>
    <span className={`text-xs shrink-0 mt-1 text-center ${isDark ? 'text-white/30' : 'text-slate-400'}`}>
      {item.source}
    </span>
  </div>
);

function usePinnedRevealEffect(pinnedContent: PinnedContentItem[]) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isRevealing, setIsRevealing] = useState(false);
  const [contentVisible, setContentVisible] = useState(true);
  const prevLengthRef = useRef(0);
  const revealTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const contentVisTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Scan-line reveal when a new item is pinned
  useEffect(() => {
    if (pinnedContent.length > prevLengthRef.current) {
      // Jump to the newly pinned item
      setCurrentIndex(pinnedContent.length - 1);
      // Start reveal: hide content briefly, then scan-line sweeps, then show
      setContentVisible(false);
      setIsRevealing(true);
      if (revealTimerRef.current) clearTimeout(revealTimerRef.current);
      if (contentVisTimerRef.current) clearTimeout(contentVisTimerRef.current);
      contentVisTimerRef.current = setTimeout(() => setContentVisible(true), 300);
      revealTimerRef.current = setTimeout(() => setIsRevealing(false), 1_600);
    }
    prevLengthRef.current = pinnedContent.length;
  }, [pinnedContent.length]); // eslint-disable-line react-hooks/exhaustive-deps

  return { currentIndex, setCurrentIndex, isRevealing, contentVisible };
}

const PaginationDots: React.FC<{
  items: PinnedContentItem[];
  safeIndex: number;
  isDark: boolean;
  onSelect: (index: number) => void;
}> = ({ items, safeIndex, isDark, onSelect }) => (
  <div className="flex items-center gap-1.5">
    {items.map((item, i) => {
      let dotButtonClasses: string;
      if (i === safeIndex) dotButtonClasses = 'bg-primary/20 text-primary';
      else dotButtonClasses = isDark ? 'text-white/30 hover:text-white/50' : 'text-slate-400 hover:text-slate-600';

      return (
        <button
          key={item.id}
          onClick={() => onSelect(i)}
          className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium transition-colors ${dotButtonClasses}`}
        >
          {getPagerIcon(item.type)}
          {i + 1}
        </button>
      );
    })}
  </div>
);

const TYPE_RENDERERS: Record<PinnedContentItem['type'], (item: PinnedContentItem, isDark: boolean) => React.ReactElement> = {
  image: (item, isDark) => <PinnedImage item={item} isDark={isDark} />,
  markdown: (item, isDark) => <PinnedMarkdown item={item} isDark={isDark} />,
  mermaid: (item, isDark) => <PinnedMermaid item={item} isDark={isDark} />,
  html: (item, isDark) => <PinnedHtml item={item} isDark={isDark} />,
};

const TaskContentWidget: React.FC<WidgetProps> = () => {
  const { state, theme } = useSecondScreenData();
  const isDark = theme === 'dark';
  const pinnedContent = state?.pinnedContent ?? [];
  const { currentIndex, setCurrentIndex, isRevealing, contentVisible } = usePinnedRevealEffect(pinnedContent);

  const goNext = useCallback(() => {
    setCurrentIndex(prev => Math.min(prev + 1, pinnedContent.length - 1));
  }, [pinnedContent.length, setCurrentIndex]);

  const goPrev = useCallback(() => {
    setCurrentIndex(prev => Math.max(prev - 1, 0));
  }, [setCurrentIndex]);

  // Clamp index if items are removed
  const safeIndex = Math.min(currentIndex, Math.max(0, pinnedContent.length - 1));
  const currentItem = pinnedContent[safeIndex];

  if (!pinnedContent.length) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isDark ? 'bg-white/5' : 'bg-slate-100'}`}>
          <ImageIcon size={20} className={isDark ? 'text-white/20' : 'text-slate-300'} />
        </div>
        <div className="text-center">
          <p className={`text-sm font-medium ${isDark ? 'text-white/40' : 'text-slate-400'}`}>
            No pinned content
          </p>
          <p className={`text-xs mt-1 ${isDark ? 'text-white/20' : 'text-slate-300'}`}>
            Push images or markdown from task outputs
          </p>
        </div>
      </div>
    );
  }

  const inactiveNavClasses = isDark ? 'text-white/10' : 'text-slate-200';
  const activeNavClasses = isDark ? 'text-white/60 hover:text-white' : 'text-slate-500 hover:text-slate-700';
  const prevButtonClasses = safeIndex === 0 ? inactiveNavClasses : activeNavClasses;
  const nextButtonClasses = safeIndex >= pinnedContent.length - 1 ? inactiveNavClasses : activeNavClasses;

  return (
    <div className="flex flex-col h-full relative">
      {/* Navigation header (only if multiple items) */}
      {pinnedContent.length > 1 && (
        <div className={`flex items-center justify-between px-3 py-1.5 shrink-0 border-b ${
          isDark ? 'border-white/10' : 'border-slate-200'
        }`}>
          <button
            onClick={goPrev}
            disabled={safeIndex === 0}
            className={`p-1 rounded transition-colors ${prevButtonClasses}`}
          >
            <ChevronLeft size={16} />
          </button>
          <PaginationDots items={pinnedContent} safeIndex={safeIndex} isDark={isDark} onSelect={setCurrentIndex} />
          <button
            onClick={goNext}
            disabled={safeIndex >= pinnedContent.length - 1}
            className={`p-1 rounded transition-colors ${nextButtonClasses}`}
          >
            <ChevronRight size={16} />
          </button>
        </div>
      )}

      {/* Content area with scan-line reveal */}
      <div className="flex-1 min-h-0 relative overflow-hidden">
        {/* Scan-line sweep overlay */}
        {isRevealing && (
          <div
            className="absolute inset-0 z-10 pointer-events-none animate-scan-line"
            style={{
              background: 'linear-gradient(to bottom, transparent 0%, rgba(0,199,196,0.12) 40%, rgba(0,199,196,0.28) 50%, rgba(0,199,196,0.12) 60%, transparent 100%)',
            }}
          />
        )}
        <div className={`h-full transition-opacity duration-500 ${contentVisible ? 'opacity-100' : 'opacity-0'}`}>
          {currentItem && TYPE_RENDERERS[currentItem.type]?.(currentItem, isDark)}
        </div>
      </div>
    </div>
  );
};

export default TaskContentWidget;
